/**
 * Runtime shims for browsers our dependencies outrun. Imported first by
 * main.tsx, so it runs before any other module evaluates.
 *
 * Promise.withResolvers (ES2024): pdf.js 4 calls it to open every PDF (the
 * contract on a public proposal, invoice previews, thumbnails). Safari before
 * 17.4 lacks it, so a homeowner on an older Mac crashed the contract viewer
 * (LIGHTMASTER-WEB-3F). pdf.js's worker runs in its own scope where this shim
 * does not reach; pdfjsWorker.ts reads NATIVE_PROMISE_WITH_RESOLVERS to give
 * those browsers pdf.js's legacy worker instead.
 */
type WithResolvers = <T>() => {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: unknown) => void;
};

const PromiseCtor = Promise as unknown as { withResolvers?: WithResolvers };

export const NATIVE_PROMISE_WITH_RESOLVERS = typeof PromiseCtor.withResolvers === 'function';

if (!NATIVE_PROMISE_WITH_RESOLVERS) {
  PromiseCtor.withResolvers = function withResolvers<T>() {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((res, rej) => { resolve = res; reject = rej; });
    return { promise, resolve, reject };
  };
}
